import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {jwtDecode} from "jwt-decode";
import Cookies from "js-cookie";
import {getAuthClientId, getAuthUrl} from "../api/Api";

type User = {
    name?: string;
    email: string;
};

export interface OAuth2TokenResponse {
    id_token: string;
    access_token: string;
    refresh_token: string;
    /** for [access_token], in seconds */
    expires_in: number;
    token_type: string;
}

export interface JwtPayload {
    iss?: string;
    sub: string;
    aud: string[] | string;
    exp: number;
    nbf?: number; // not present
    iat: number;
    jti: string;
    at_hash: string;
    "cognito:groups": string[];
    email_verified: boolean;
    "cognito:username": string;
    origin_jti: string;
    event_id: string;
    token_use: string;
    auth_time: number;
    email: string;
    name?: string;
}

type AuthContextType = {
    user?: User;

    login: (oAuth2TokenResponse?: OAuth2TokenResponse) => void;
    logout: () => void;
    getAccessToken: () => Promise<string>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const dev = process.env.NODE_ENV === "development";

    const name = Cookies.get("user_name")
    const email = Cookies.get("user_email")
    let defaultUser: User | undefined
    if (email) {
        defaultUser = {
            name: name,
            email: email
        }
    } else {
        defaultUser = undefined;
    }

    const [user, setUser] = useState<User | undefined>(defaultUser);

    useEffect(() => {
        const channel = new BroadcastChannel("auth");
        channel.onmessage = (msg) => {
            if (msg.data.event === "LOGIN") {


                const name = Cookies.get("user_name")
                const email = Cookies.get("user_email")
                if (email) {
                    setUser({
                        name: name,
                        email: email
                    });
                }
            }
            if (msg.data.event === "LOGOUT") {
                setUser(undefined);
            }
        };
        return () => channel.close();
    }, [setUser]);

    const login = (json?: OAuth2TokenResponse) => {
        if (!json) return;
        const decoded = jwtDecode<JwtPayload>(json.id_token)

        Cookies.set("id_token", json.id_token, {sameSite: 'Strict', secure: !dev});
        Cookies.set("access_token", json.access_token, {sameSite: 'Strict', secure: !dev});
        Cookies.set("refresh_token", json.refresh_token, {sameSite: 'Strict', secure: !dev});

        const now = new Date()
        const nowMillis = now.getTime()
        const expiresAtMillis = nowMillis + (json.expires_in * 1000)

        Cookies.set("expires_at", expiresAtMillis.toString(10), {sameSite: 'Strict', secure: !dev});
        if (decoded.name) {
            Cookies.set("user_name", decoded.name, {sameSite: 'Strict', secure: !dev});
        }
        Cookies.set("user_email", decoded.email, {sameSite: 'Strict', secure: !dev});

        setUser({
            name: decoded.name,
            email: decoded.email
        });

        const channel = new BroadcastChannel("auth");
        channel.postMessage({ event: "LOGIN" });
        channel.close();
    };

    const logout = () => {
        Cookies.remove("id_token", {sameSite: 'Strict', secure: !dev})
        Cookies.remove("access_token", {sameSite: 'Strict', secure: !dev})
        Cookies.remove("refresh_token", {sameSite: 'Strict', secure: !dev})
        Cookies.remove("expires_at", {sameSite: 'Strict', secure: !dev})
        Cookies.remove("user_name", {sameSite: 'Strict', secure: !dev})
        Cookies.remove("user_email", {sameSite: 'Strict', secure: !dev})

        setUser(undefined);

        const channel = new BroadcastChannel("auth");
        channel.postMessage({ event: "LOGOUT" });
        channel.close();
    };

    const getAccessToken = async () => {
        // todo make it singleton
        const currentAccessToken = Cookies.get("access_token")!!

        const now = new Date()
        const nowMillis = now.getTime()
        const expiresAt = Number(Cookies.get("expires_at")!!)
        if (expiresAt < nowMillis) {
            const body = new URLSearchParams({
                refresh_token: Cookies.get("refresh_token")!!,
                grant_type: 'refresh_token',
                client_id: getAuthClientId()
            });

            try {
                const response = await fetch(`${getAuthUrl()}/oauth2/token`, {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                    },
                    body: body
                })

                const json: OAuth2TokenResponse = await response.json()
                login(json)
                return Cookies.get("access_token")!!
            } catch (e) {
                logout()
                // todo navigate to home?
                throw e;
            }
        } else if (expiresAt - nowMillis < 1000 * 60 * 5) {
            // todo refresh in background
            return currentAccessToken
        } else {
            return currentAccessToken
        }
    }

    return (
        <AuthContext.Provider value={{ user, login, logout, getAccessToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}